/**
 * React Query hooks for company financial data.
 *
 * Backend caches for 24 hours, so we use a 30-minute stale time here.
 * The full financial response (~10 years of data) is fetched once and cached.
 */

import { useQuery } from '@tanstack/react-query';
import {
  getCompanyFinancials,
  getCompanySummary,
  getAllSummary,
  getProjectFinancials,
  getProjectComparison,
  getCompanyMetricCards,
  getCompanyDashboard,
  getProjectDashboard,
  getCompanyTrend,
  compareCompanies,
  getFinancialMetricsComparison,
} from '../../../lib/api/financialApi';
import type {
  UnifiedFinancialRecord,
  MetricCardsResponse,
  DashboardTabResponse,
  ProjectDashboardTabResponse,
  CompanyTrendResponse,
  CompanyCompareResponse,
  FinancialMetricsComparisonResponse,
} from '../../../types/financial';

const STALE_TIME_30_MIN = 1000 * 60 * 30;
const STALE_TIME_1_HOUR = 1000 * 60 * 60;

// ============================================================================
// Company Financials
// ============================================================================

/**
 * Fetch full financial time series for a company.
 *
 * @param companyName - Company name as stored in MongoDB (case-insensitive lookup)
 * @param years       - Number of fiscal years to retrieve (default 10)
 * @param country     - Optional country override ("INDIA" | "US")
 */
export const useCompanyFinancials = (
  companyName: string | null | undefined,
  years: number = 10,
  country?: string
) =>
  useQuery({
    queryKey: ['financial', 'company', companyName, years, country],
    queryFn: () => getCompanyFinancials(companyName!, years, country),
    enabled: !!companyName,
    staleTime: STALE_TIME_30_MIN,
    select: (data) => {
      // Collapse to one record per fiscal year, then sort ascending for charts.
      //
      // The dedupe is not cosmetic: `year` is the React key of every column in
      // the financial tables (Financial Summary, P&L, Balance Sheet, Valuation)
      // and the x-axis key of every chart. A repeated year produces duplicate
      // keys, which breaks React reconciliation — the symptom is a table that
      // renders MORE columns than there are records, repeated and out of order,
      // and that does not correct itself on re-render.
      //
      // The backend collapses duplicates too; this is the client-side guarantee
      // so a stale cache or an older API build cannot corrupt the tables.
      const byYear = new Map<number, UnifiedFinancialRecord>();
      for (const rec of data.financial_data || []) {
        const existing = byYear.get(rec.year);
        // Keep the richer record when a year repeats.
        if (
          !existing ||
          Object.values(rec).filter((v) => v != null).length >
            Object.values(existing).filter((v) => v != null).length
        ) {
          byYear.set(rec.year, rec);
        }
      }
      const sorted = [...byYear.values()].sort(
        (a: UnifiedFinancialRecord, b: UnifiedFinancialRecord) => a.year - b.year
      );
      return { ...data, financial_data: sorted };
    },
  });

/**
 * Fetch lightweight summary (latest year KPIs only) for a company.
 * Used for company list cards/badges.
 */
export const useCompanySummary = (
  companyName: string | null | undefined,
  country?: string
) =>
  useQuery({
    queryKey: ['financial', 'summary', companyName, country],
    queryFn: () => getCompanySummary(companyName!, country),
    enabled: !!companyName,
    staleTime: STALE_TIME_1_HOUR,
    // Don't retry 404s — company simply hasn't been ingested yet.
    // Without this, each missing company fires 3 retries × MongoDB reconnect (~500ms each).
    retry: (count, error: any) => {
      const status = error?.response?.status ?? error?.status;
      return status !== 404 && count < 1;
    },
  });

/**
 * Fetch lightweight summaries for multiple companies in a single request.
 * Replaces N individual getCompanySummary calls with one bulk call.
 */
export const useAllSummary = (
  companies: string[] | null | undefined,
  country?: string
) =>
  useQuery({
    queryKey: ['financial', 'bulk-summary', companies, country],
    queryFn: () => getAllSummary(companies!, country),
    enabled: !!companies && companies.length > 0,
    staleTime: STALE_TIME_1_HOUR,
    retry: (count, error: any) => {
      const status = error?.response?.status ?? error?.status;
      return status !== 404 && count < 1;
    },
  });

// ============================================================================
// Project Financials
// ============================================================================

/**
 * Fetch financial data for all companies in a project.
 */
export const useProjectFinancials = (
  projectId: string | null | undefined,
  years: number = 10
) =>
  useQuery({
    queryKey: ['financial', 'project', projectId, years],
    queryFn: () => getProjectFinancials(projectId!, years),
    enabled: !!projectId,
    staleTime: STALE_TIME_30_MIN,
  });

/**
 * Fetch competitor comparison table for a project.
 */
export const useProjectComparison = (
  projectId: string | null | undefined,
  selectedCompanyId?: string,
  fiscalYear?: number
) =>
  useQuery({
    queryKey: ['financial', 'comparison', projectId, selectedCompanyId, fiscalYear],
    queryFn: () => getProjectComparison(projectId!, selectedCompanyId, fiscalYear),
    enabled: !!projectId,
    staleTime: STALE_TIME_30_MIN,
  });

// ============================================================================
// Metric Cards
// ============================================================================

/**
 * Fetch KPI metric cards for a company (latest year + CAGR).
 * Lighter-weight alternative to useCompanyFinancials when only cards are needed.
 */
export const useCompanyMetricCards = (
  companyName: string | null | undefined,
  country?: string,
  years: number = 5
) =>
  useQuery<MetricCardsResponse>({
    queryKey: ['financial', 'metric-cards', companyName, country, years],
    queryFn: () => getCompanyMetricCards(companyName!, country, years),
    enabled: !!companyName,
    staleTime: STALE_TIME_1_HOUR,
    retry: (count, error: any) => {
      const status = error?.response?.status ?? error?.status;
      return status !== 404 && count < 1;
    },
  });

// ============================================================================
// Dashboard Tabs
// ============================================================================

/**
 * Fetch pre-packaged dashboard data for a specific company tab.
 */
export const useCompanyDashboard = (
  companyName: string | null | undefined,
  tab: 'overview' | 'financials' | 'valuation' | 'sensitivity',
  years: number = 10,
  country?: string
) =>
  useQuery<DashboardTabResponse>({
    queryKey: ['financial', 'dashboard', companyName, tab, years, country],
    queryFn: () => getCompanyDashboard(companyName!, tab, years, country),
    enabled: !!companyName,
    staleTime: STALE_TIME_30_MIN,
  });

/**
 * Fetch pre-packaged dashboard data for a project tab.
 */
export const useProjectDashboard = (
  projectId: string | null | undefined,
  tab: 'overview' | 'financials' | 'valuation' | 'sensitivity',
  years: number = 10
) =>
  useQuery<ProjectDashboardTabResponse>({
    queryKey: ['financial', 'project-dashboard', projectId, tab, years],
    queryFn: () => getProjectDashboard(projectId!, tab, years),
    enabled: !!projectId,
    staleTime: STALE_TIME_30_MIN,
  });

// ============================================================================
// Trend Analysis
// ============================================================================

/**
 * Fetch year-on-year trend for a single named metric.
 */
export const useCompanyTrend = (
  companyName: string | null | undefined,
  metric: string,
  years: number = 10,
  country?: string
) =>
  useQuery<CompanyTrendResponse>({
    queryKey: ['financial', 'trend', companyName, metric, years, country],
    queryFn: () => getCompanyTrend(companyName!, metric, years, country),
    enabled: !!companyName && !!metric,
    staleTime: STALE_TIME_30_MIN,
  });

// ============================================================================
// Company Compare
// ============================================================================

/**
 * Fetch side-by-side KPI comparison for multiple companies.
 */
export const useCompanyCompare = (
  companyNames: string[] | null | undefined,
  years: number = 5
) =>
  useQuery<CompanyCompareResponse>({
    queryKey: ['financial', 'compare', companyNames, years],
    queryFn: () => compareCompanies(companyNames!, years),
    enabled: !!companyNames && companyNames.length > 0,
    staleTime: STALE_TIME_30_MIN,
  });

// ============================================================================
// Financial Metrics Comparison (peer analysis)
// ============================================================================

/**
 * Fetch 4-metric peer comparison (Revenue CAGR, EBITDA, ROIC, WACC)
 * for a focal company vs named competitors.
 */
export const useFinancialMetricsComparison = (
  companyName: string | null | undefined,
  competitors?: string[],
  years: number = 10
) =>
  useQuery<FinancialMetricsComparisonResponse>({
    queryKey: ['financial', 'metrics-comparison', companyName, competitors, years],
    queryFn: () => getFinancialMetricsComparison({ company_name: companyName!, competitors, years }),
    enabled: !!companyName,
    staleTime: STALE_TIME_1_HOUR,
  });
